/* eslint-disable react/prop-types */

import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import Button from "components/atoms/Button/Button";
import useTableRequest from "hooks/useTableRequest";
import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";
import DataTable from "components/organisms/DataTable/DataTable";
import { getCompanyApprovalLevels, deleteAllApprovalLevels } from "api";
import Dialog from "components/organisms/Dialog/Dialog";
import usePermissions from "hooks/usePermissions";
import NewApprovalDialog from "./NewApprovalDialog";

function LocationApprovalLevels() {
  const intl = useIntl();
  const { location: paymentLocation } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [levelId, setLevelId] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [paymentLocationName, setPaymentLocationName] = useState("");
  const { permissions } = usePermissions();
  const adminSettings = JSON.parse(permissions || "{}")?.permissions?.admin_settings;
  const viewOnly = Boolean(!adminSettings?.edit_approval_levels);

  const columns = [
    {
      Header: "Level",
      accessor: "name",
      sortable: true,
      Cell: ({ value }) => <p className="pl-9 pt-8 pb-8 mb-0 mr-1 font-weight-bold">{value}</p>,
    },
    {
      Header: "Order",
      accessor: "order",
      sortable: true,
      Cell: ({ value }) => <p className="pl-9 pt-8 pb-8 mb-0 mr-1">{value}</p>,
    },
    {
      Header: "Amount",
      accessor: "amount",
      sortable: true,
      Cell: ({ value }) => <p className="pl-9 pt-8 pb-8 mb-0 mr-1">{value}</p>,
    },
    {
      Header: "Members",
      accessor: "members",
      sortable: true,
      Cell: ({ value }) => {
        const members = value.map((member) => member).join(", ");
        return <p className="pl-9 pt-8 pb-8 mb-0 mr-1">{members}</p>;
      },
    },
  ];

  const { instance, fetching, totalData, refetch } = useTableRequest(getCompanyApprovalLevels, columns, {
    filters: (globalFilter) => ({
      ...(globalFilter.query?.length && { query: globalFilter.query }),
      location: paymentLocation,
    }),
    deps: [paymentLocation],
  });

  const handleCloseDialog = () => {
    setLevelId(null);
    setShowDialog(false);
  };

  // create a Dialog as a confirmation to delete all levels
  const handleDeleteAllLevels = () => {
    if (viewOnly) return;
    deleteAllApprovalLevels(paymentLocation).then(() => {
      setShowDeleteDialog(false);
      refetch();
    });
  };

  function ConfirmDeleteDialog() {
    return (
      <Dialog
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        title={intl.formatMessage({
          id: "settings.approval-level.deleteAllApprovalLevels",
          defaultMessage: "Delete all approval levels",
        })}
      >
        <div style={{ width: "450px" }}>
          <p>
            <FormattedMessage
              id="settings.approval-level.deleteAllApprovalLevels.confirmation"
              defaultMessage="Are you sure you want to delete all approval levels for this location?"
            />
          </p>
          <p className="text-secondary">
            <FormattedMessage
              id="settings.approval-level.deleteAllApprovalLevels.message"
              defaultMessage="Users associated with these approval levels might not be able to book shipments or give approval. Please, make sure to review the approval levels for these users."
            />
          </p>
          <div className="d-flex justify-content-between">
            <Button variant="outline-primary" onClick={() => setShowDeleteDialog(false)}>
              <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
            </Button>
            <Button variant="danger" onClick={handleDeleteAllLevels}>
              <FormattedMessage id="general.delete" defaultMessage="Delete" />
            </Button>
          </div>
        </div>
      </Dialog>
    );
  }

  const handleBackButton = () => {
    navigate(-1);
  };

  useEffect(() => {
    setPaymentLocationName(location?.state?.name);
    window.addEventListener("popstate", handleBackButton);
    return () => {
      window.removeEventListener("popstate", handleBackButton);
    };
  }, []);

  return (
    <div>
      <NewApprovalDialog
        totalOrders={instance?.data?.length ? instance.data.length : 1}
        refetch={refetch}
        id={levelId}
        close={handleCloseDialog}
        isOpen={showDialog}
        isLocation
        viewOnly={viewOnly}
      />
      {showDeleteDialog && <ConfirmDeleteDialog />}

      <DataTable
        tableInstance={instance}
        totalData={totalData}
        isLoading={fetching}
        draggable={false}
        onRowClick={(row) => {
          setLevelId(row.original.id);
          setShowDialog(true);
        }}
        headerLeft={
          <div>
            <div className="font-weight-bold" style={{ fontSize: "24px", marginBottom: "16px", marginTop: "8px" }}>
              Location level: {paymentLocationName}
            </div>
            <div className="w-30-px">
              <GlobalTableSearch
                currentFilters={instance.state.globalFilter}
                setFilters={instance.setGlobalFilter}
                filterKey="query"
                eventContext="Location level"
              />
            </div>
          </div>
        }
        headerRight={
          !viewOnly && (
            <div className="d-flex align-items-center">
              <Button variant="danger" className="mr-3" onClick={() => setShowDeleteDialog(true)}>
                <FormattedMessage
                  id="settings.approval-level.deleteAllApprovalLevels"
                  defaultMessage="Delete all approval levels"
                />
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => {
                  setLevelId(null);
                  setShowDialog(true);
                }}
              >
                <FormattedMessage
                  id="settings.approval-level.newSpecificApprovalLevel"
                  defaultMessage="New specific approval level"
                />
              </Button>
            </div>
          )
        }
      />
    </div>
  );
}

export default LocationApprovalLevels;
